/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import glamorous from 'glamorous';

import t from 'app/utils/translate';
import { SCHEDULED, OPEN, IN_MANUAL_REVIEW, IN_PROGRESS } from 'app/constants/challengeStatusTypes';
import ChallengeStatusToggleBox from 'app/components/challenge/ChallengeStatusToggleBox';
import MatchUpLine from 'app/components/challenge/MatchUpLine';
import StatusCheckButton from 'app/components/commons/StatusCheckButton';
import FilterLabel from 'app/components/commons/FilterLabel';
import ResetButton from 'app/components/commons/ResetButton';
import {
  resetFilters,
  toggleGamesFilter,
  toggleGamesStatusFilter,
  setPlayerTypeFilter
} from '../../actions/playerListActions';

type Props = {
  filteredGames: Array<string>,
  games: Array<Object>,
  showGamesFilter?: boolean,
  onGamesChange: string => void,
  onReset: () => void,
  onStatusesChange: () => void,
  selectedStatuses: Array<Object>,
  showStatusFilters: boolean
};

const defaultProps = {
  showGamesFilter: true
};

const Wrapper = glamorous.div(({ theme }) => ({
  padding: 10,
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    fontSize: '0.75rem'
  }
}));

const GamesRow = glamorous.div({
  display: 'flex',
  flexBasis: '100%',
  flexGrow: 0,
  flexShrink: 0,
  flexWrap: 'wrap',
  marginLeft: -5,
  marginRight: -5,
  paddingBottom: 5
});

const Row = glamorous.div({
  display: 'flex',
  flexBasis: '100%',
  flexGrow: 0,
  flexShrink: 0,
  justifyContent: 'space-between'
});

const ButtonItem = glamorous.div(({ theme }) => ({
  flexBasis: '33%',
  flexGrow: 0,
  flexShrink: 0,
  padding: 5,
  whiteSpace: 'nowrap',
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    flexBasis: '25%'
  },
  [theme.mediaQuery.desktopAndUp]: {
    flexBasis: '20%'
  }
}));

const ContentFinal = glamorous.div({
  display: 'flex',
  alignSelf: 'flex-end'
});

function LiveChallengeFilterContent({
  filteredGames,
  games,
  onGamesChange,
  onReset,
  onStatusesChange,
  selectedStatuses,
  showGamesFilter,
  showStatusFilters
}: Props) {
  return (
    <Wrapper>
      {showGamesFilter && games.length > 1 && (
        <div>
          <FilterLabel scheme="light">{t('Games')}</FilterLabel>
          <GamesRow>
            {games.map(game => (
              <ButtonItem key={game.id}>
                <StatusCheckButton
                  onChange={() => onGamesChange(game.id)}
                  checked={filteredGames.includes(game.id)}
                  statusId={game.statusId}
                >
                  <MatchUpLine
                    awayTeam={game.awayAbbr}
                    gameName={game.name}
                    homeTeam={game.homeAbbr}
                    reverseOrder={game.countryCode === 'US'}
                  />
                </StatusCheckButton>
              </ButtonItem>
            ))}
          </GamesRow>
        </div>
      )}
      {showStatusFilters && (
        <Row>
          <div>
            <FilterLabel scheme="light">{t('Status')}</FilterLabel>
            <div>
              <ChallengeStatusToggleBox
                onChange={onStatusesChange}
                selectedStatuses={selectedStatuses}
              />
            </div>
          </div>
        </Row>
      )}
      <Row>
        <ContentFinal>
          <ResetButton label="Reset&nbsp;Filters" onClick={onReset} scheme="dark" />
        </ContentFinal>
      </Row>
    </Wrapper>
  );
}

LiveChallengeFilterContent.defaultProps = defaultProps;

const connector = connect(
  state => ({
    filteredGames: state.playerList.filteredGames,
    games: state.challenge.games,
    selectedStatuses: state.playerList.gameStatusFilters,
    showStatusFilters: state.challenge.games.length > 1,
    playerTypeFilters: state.playerList.playerTypeFilters
  }),
  {
    onGamesChange: toggleGamesFilter,
    onReset: resetFilters,
    onIncludedChange() {
      return setPlayerTypeFilter('included');
    },
    onStatusesChange(status) {
      if (status === OPEN) {
        return toggleGamesStatusFilter([SCHEDULED, OPEN]);
      }
      if (status === IN_PROGRESS) {
        return toggleGamesStatusFilter([IN_PROGRESS, IN_MANUAL_REVIEW]);
      }
      return toggleGamesStatusFilter([status]);
    }
  }
);

export default connector(LiveChallengeFilterContent);
